/**
 * Browser document scanner with edge detection and perspective correction.
 * Based on jscanify (MIT License) by ColonelParrot.
 * Requires OpenCV.js loaded globally (window.cv).
 */

declare const cv: any;

function distance(p1: { x: number; y: number }, p2: { x: number; y: number }): number {
    return Math.hypot(p1.x - p2.x, p1.y - p2.y);
}

interface CornerPoints {
    topLeftCorner?: { x: number; y: number };
    topRightCorner?: { x: number; y: number };
    bottomLeftCorner?: { x: number; y: number };
    bottomRightCorner?: { x: number; y: number };
}

export default class Scanner {
    findPaperContour(img: any): any | null {
        const imgGray = new cv.Mat();
        cv.Canny(img, imgGray, 50, 200);

        const imgBlur = new cv.Mat();
        cv.GaussianBlur(imgGray, imgBlur, new cv.Size(3, 3), 0, 0, cv.BORDER_DEFAULT);

        const imgThresh = new cv.Mat();
        cv.threshold(imgBlur, imgThresh, 0, 255, cv.THRESH_OTSU);

        const contours = new cv.MatVector();
        const hierarchy = new cv.Mat();
        cv.findContours(imgThresh, contours, hierarchy, cv.RETR_CCOMP, cv.CHAIN_APPROX_SIMPLE);

        let maxArea = 0;
        let maxContourIndex = -1;
        for (let i = 0; i < contours.size(); ++i) {
            const contourArea = cv.contourArea(contours.get(i));
            if (contourArea > maxArea) {
                maxArea = contourArea;
                maxContourIndex = i;
            }
        }

        const maxContour = maxContourIndex >= 0 ? contours.get(maxContourIndex) : null;

        imgGray.delete();
        imgBlur.delete();
        imgThresh.delete();
        contours.delete();
        hierarchy.delete();
        return maxContour;
    }

    highlightPaper(
        image: HTMLCanvasElement,
        options?: { color?: string; thickness?: number }
    ): HTMLCanvasElement {
        const opts = { color: '#00ff00', thickness: 3, ...options };
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;
        const img = cv.imread(image);

        const maxContour = this.findPaperContour(img);
        cv.imshow(canvas, img);

        if (maxContour) {
            const corners = this.getCornerPoints(maxContour);
            if (corners.topLeftCorner && corners.topRightCorner && corners.bottomLeftCorner && corners.bottomRightCorner) {
                ctx.strokeStyle = opts.color;
                ctx.lineWidth = opts.thickness;
                ctx.beginPath();
                ctx.moveTo(corners.topLeftCorner.x, corners.topLeftCorner.y);
                ctx.lineTo(corners.topRightCorner.x, corners.topRightCorner.y);
                ctx.lineTo(corners.bottomRightCorner.x, corners.bottomRightCorner.y);
                ctx.lineTo(corners.bottomLeftCorner.x, corners.bottomLeftCorner.y);
                ctx.lineTo(corners.topLeftCorner.x, corners.topLeftCorner.y);
                ctx.stroke();
            }
        }

        img.delete();
        return canvas;
    }

    extractPaper(
        image: HTMLCanvasElement,
        resultWidth: number,
        resultHeight: number
    ): HTMLCanvasElement | null {
        const canvas = document.createElement('canvas');
        const img = cv.imread(image);
        const maxContour = this.findPaperContour(img);

        if (!maxContour) {
            img.delete();
            return null;
        }

        const corners = this.getCornerPoints(maxContour);
        if (!corners.topLeftCorner || !corners.topRightCorner || !corners.bottomLeftCorner || !corners.bottomRightCorner) {
            img.delete();
            return null;
        }

        const warpedDst = new cv.Mat();
        const dsize = new cv.Size(resultWidth, resultHeight);
        const srcTri = cv.matFromArray(4, 1, cv.CV_32FC2, [
            corners.topLeftCorner.x, corners.topLeftCorner.y,
            corners.topRightCorner.x, corners.topRightCorner.y,
            corners.bottomLeftCorner.x, corners.bottomLeftCorner.y,
            corners.bottomRightCorner.x, corners.bottomRightCorner.y,
        ]);
        const dstTri = cv.matFromArray(4, 1, cv.CV_32FC2, [
            0, 0,
            resultWidth, 0,
            0, resultHeight,
            resultWidth, resultHeight,
        ]);

        const M = cv.getPerspectiveTransform(srcTri, dstTri);
        cv.warpPerspective(img, warpedDst, M, dsize, cv.INTER_LINEAR, cv.BORDER_CONSTANT, new cv.Scalar());
        cv.imshow(canvas, warpedDst);

        img.delete();
        warpedDst.delete();
        return canvas;
    }

    getCornerPoints(contour: any): CornerPoints {
        const rect = cv.minAreaRect(contour);
        const center = rect.center;

        let topLeftCorner: { x: number; y: number } | undefined;
        let topLeftCornerDist = 0;
        let topRightCorner: { x: number; y: number } | undefined;
        let topRightCornerDist = 0;
        let bottomLeftCorner: { x: number; y: number } | undefined;
        let bottomLeftCornerDist = 0;
        let bottomRightCorner: { x: number; y: number } | undefined;
        let bottomRightCornerDist = 0;

        for (let i = 0; i < contour.data32S.length; i += 2) {
            const point = { x: contour.data32S[i], y: contour.data32S[i + 1] };
            const dist = distance(point, center);
            if (point.x < center.x && point.y < center.y) {
                if (dist > topLeftCornerDist) { topLeftCorner = point; topLeftCornerDist = dist; }
            } else if (point.x > center.x && point.y < center.y) {
                if (dist > topRightCornerDist) { topRightCorner = point; topRightCornerDist = dist; }
            } else if (point.x < center.x && point.y > center.y) {
                if (dist > bottomLeftCornerDist) { bottomLeftCorner = point; bottomLeftCornerDist = dist; }
            } else if (point.x > center.x && point.y > center.y) {
                if (dist > bottomRightCornerDist) { bottomRightCorner = point; bottomRightCornerDist = dist; }
            }
        }

        return { topLeftCorner, topRightCorner, bottomLeftCorner, bottomRightCorner };
    }
}
